import { ReactNode, useState } from "react";
import { AssessmentType, ComplianceProgramStep } from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, isAdminVar } from "@cache/cache";

import {
  ProgramFormStepDownwardReview,
  ProgramFormStepPeerReview,
} from "../types";
import CoreValueAssessmentSettingsModal from "./core-value-assessment-settings-modal";
import GoalAssessmentSettingsModal from "./goal-assessment-settings-modal";
import { QuestionListItem, QuestionRow } from "./question-tab-section";
import RoleBasedAssessmentSettingsModal from "./role-based-assessment-settings-modal";
import {
  useCoreValuesSummary,
  useGoalsSummary,
  useRolesSummary,
} from "./system-question-summaries";

// The Roles and Responsibilities, Goal Progression and Core Values rows, with
// the settings each opens. Self and manager reviews and peer reviews both
// offer them. The rows go to children, so the card can list them with the
// review's other parts, in order.
const RoleGoalValueQuestionRows = <
  Step extends ProgramFormStepPeerReview | ProgramFormStepDownwardReview,
>({
  step,
  canUpdate,
  onAddOrUpdateStep,
  children,
}: {
  step: Step;
  canUpdate: boolean;
  onAddOrUpdateStep: (step: Step) => void;
  children: (items: QuestionListItem[]) => ReactNode;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const isAdmin = isAdminVar();
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [isCoreValueModalOpen, setIsCoreValueModalOpen] = useState(false);
  const rolesSummary = useRolesSummary(step.roleBasedReviewConfig);
  const goalsSummary = useGoalsSummary(step.goalReviewConfig);
  const coreValuesSummary = useCoreValuesSummary(step.coreValueReviewConfig);

  const isPeerReview = step.stepType === ComplianceProgramStep.PeerReview;
  const assessmentType = isPeerReview
    ? AssessmentType.Peer
    : AssessmentType.Performance;
  // Ends the warnings shown when the organization has nothing to review against.
  const settingsHint = isAdmin
    ? ` Go to ${label("organization", { capitalize: true })} settings to define them.`
    : "";

  const rolesWarning = organization.hasCareerFramework
    ? undefined
    : `There are no Career Tracks defined for your ${label(
        "organization",
      )}.${settingsHint}`;
  const coreValuesWarning =
    organization.coreValues?.edges.length === 0
      ? `There are no Core Values defined for your ${label(
          "organization",
        )}.${settingsHint}`
      : undefined;

  const rolesTitle = "Roles and Responsibilities";
  const goalsTitle = `${label("goal", { capitalize: true })} Progression`;
  const coreValuesTitle = "Core Values";

  const setRolesEnabled = (enabled: boolean) =>
    onAddOrUpdateStep({
      ...step,
      roleBasedReviewConfig: {
        ...step.roleBasedReviewConfig,
        enabledForCurrentRole: enabled,
        // Only self and manager reviews can rate the next role, and only with
        // role questions on: the backend will not save next-role questions
        // without them.
        ...((isPeerReview || !enabled) && {
          enabledForNextRole: false,
          enabledSelfReviewForNextRole: false,
        }),
      },
    });
  const setGoalsEnabled = (enabled: boolean) =>
    onAddOrUpdateStep({
      ...step,
      goalReviewConfig: { ...step.goalReviewConfig, enabled },
    });
  const setCoreValuesEnabled = (enabled: boolean) =>
    onAddOrUpdateStep({
      ...step,
      coreValueReviewConfig: { ...step.coreValueReviewConfig, enabled },
    });

  const items: QuestionListItem[] = [
    {
      key: "roles",
      added: step.roleBasedReviewConfig.enabledForCurrentRole,
      unavailable: !!rolesWarning,
      row: (
        <QuestionRow
          title={rolesTitle}
          summary={rolesSummary}
          warning={rolesWarning}
          warningSummary="No Career Tracks defined yet"
          added={step.roleBasedReviewConfig.enabledForCurrentRole}
          disabled={!canUpdate}
          onRemove={() => setRolesEnabled(false)}
          onEdit={() => setIsRoleModalOpen(true)}
          onAdd={() => setIsRoleModalOpen(true)}
        />
      ),
    },
    ...(organization.featureFlags.goals
      ? [
          {
            key: "goals",
            added: step.goalReviewConfig.enabled,
            row: (
              <QuestionRow
                title={goalsTitle}
                summary={goalsSummary}
                added={step.goalReviewConfig.enabled}
                disabled={!canUpdate}
                onRemove={() => setGoalsEnabled(false)}
                onEdit={() => setIsGoalModalOpen(true)}
                onAdd={() => setIsGoalModalOpen(true)}
              />
            ),
          },
        ]
      : []),
    {
      key: "core-values",
      added: step.coreValueReviewConfig.enabled,
      unavailable: !!coreValuesWarning,
      row: (
        <QuestionRow
          title={coreValuesTitle}
          summary={coreValuesSummary}
          warning={coreValuesWarning}
          warningSummary="No Core Values defined yet"
          added={step.coreValueReviewConfig.enabled}
          disabled={!canUpdate}
          onRemove={() => setCoreValuesEnabled(false)}
          onEdit={() => setIsCoreValueModalOpen(true)}
          onAdd={() => setIsCoreValueModalOpen(true)}
        />
      ),
    },
  ];

  return (
    <>
      {isRoleModalOpen && (
        <RoleBasedAssessmentSettingsModal
          title={rolesTitle}
          isEnabling={!step.roleBasedReviewConfig.enabledForCurrentRole}
          assessmentType={assessmentType}
          onClose={() => setIsRoleModalOpen(false)}
          onSave={() => {
            setRolesEnabled(true);
            setIsRoleModalOpen(false);
          }}
          canUpdate={canUpdate}
          selectedPerformanceAssessmentGroup={step}
          onChangeSettings={(settings) => {
            onAddOrUpdateStep({
              ...step,
              roleBasedReviewConfig: {
                ...step.roleBasedReviewConfig,
                ...settings,
              },
            });
          }}
        />
      )}
      {isGoalModalOpen && (
        <GoalAssessmentSettingsModal
          title={goalsTitle}
          isEnabling={!step.goalReviewConfig.enabled}
          assessmentType={assessmentType}
          onClose={() => setIsGoalModalOpen(false)}
          onSave={() => {
            setGoalsEnabled(true);
            setIsGoalModalOpen(false);
          }}
          canUpdate={canUpdate}
          selectedPerformanceAssessmentGroup={step}
          onChangeSettings={(settings) => {
            onAddOrUpdateStep({
              ...step,
              goalReviewConfig: { ...step.goalReviewConfig, ...settings },
            });
          }}
        />
      )}
      {isCoreValueModalOpen && (
        <CoreValueAssessmentSettingsModal
          title={coreValuesTitle}
          isEnabling={!step.coreValueReviewConfig.enabled}
          assessmentType={assessmentType}
          onClose={() => setIsCoreValueModalOpen(false)}
          onSave={() => {
            setCoreValuesEnabled(true);
            setIsCoreValueModalOpen(false);
          }}
          canUpdate={canUpdate}
          selectedPerformanceAssessmentGroup={step}
          onChangeSettings={(settings) => {
            onAddOrUpdateStep({
              ...step,
              coreValueReviewConfig: {
                ...step.coreValueReviewConfig,
                ...settings,
              },
            });
          }}
        />
      )}

      {children(items)}
    </>
  );
};

export default RoleGoalValueQuestionRows;
